/**
 * Player preferences shared by every video surface (session detail, public
 * /watch, analytics). Deliberately global rather than per-session: /watch
 * serves anonymous viewers who have no session-scoped settings bucket, and a
 * "stay on 360p" choice is about the viewer's connection, not the session.
 *
 * Distinct from `useAnalyticsSettings`, which persists per-session workspace
 * state (chart viewport, selected channels, sync offset).
 */

const STORAGE_KEY = "rm.videoPlayer.prefs";

export const PLAYBACK_RATES = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2] as const;

export interface VideoPreferences {
  /** Manually pinned rendition height, or null for adaptive (Auto). */
  qualityHeight: number | null;
  volume: number;
  isMuted: boolean;
  playbackRate: number;
}

export const DEFAULT_VIDEO_PREFERENCES: VideoPreferences = {
  qualityHeight: null,
  volume: 1,
  isMuted: false,
  playbackRate: 1,
};

const clampVolume = (value: number): number => Math.min(1, Math.max(0, value));

/**
 * Coerces an untrusted parsed blob into a valid preferences object. Every
 * field falls back independently so that one corrupt key doesn't discard the
 * others, and unknown playback rates are dropped rather than applied — a
 * hand-edited `playbackRate: 50` would otherwise be unrecoverable through the
 * UI, which only offers PLAYBACK_RATES.
 */
function normalize(raw: unknown): VideoPreferences {
  if (typeof raw !== "object" || raw === null) {
    return { ...DEFAULT_VIDEO_PREFERENCES };
  }
  const record = raw as Record<string, unknown>;

  const qualityHeight =
    typeof record.qualityHeight === "number" &&
    Number.isFinite(record.qualityHeight) &&
    record.qualityHeight > 0
      ? record.qualityHeight
      : null;

  const volume =
    typeof record.volume === "number" && Number.isFinite(record.volume)
      ? clampVolume(record.volume)
      : DEFAULT_VIDEO_PREFERENCES.volume;

  const playbackRate = PLAYBACK_RATES.includes(
    record.playbackRate as (typeof PLAYBACK_RATES)[number],
  )
    ? (record.playbackRate as number)
    : DEFAULT_VIDEO_PREFERENCES.playbackRate;

  return {
    qualityHeight,
    volume,
    isMuted:
      typeof record.isMuted === "boolean"
        ? record.isMuted
        : DEFAULT_VIDEO_PREFERENCES.isMuted,
    playbackRate,
  };
}

export function readVideoPreferences(): VideoPreferences {
  if (typeof window === "undefined") {
    return { ...DEFAULT_VIDEO_PREFERENCES };
  }
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return { ...DEFAULT_VIDEO_PREFERENCES };
    }
    return normalize(JSON.parse(stored));
  } catch {
    // Malformed JSON, or storage blocked entirely (Safari private mode,
    // third-party-context iframes). Preferences are a convenience; never let
    // them break playback.
    return { ...DEFAULT_VIDEO_PREFERENCES };
  }
}

export function writeVideoPreferences(
  update: Partial<VideoPreferences>,
): VideoPreferences {
  const next = { ...readVideoPreferences(), ...update };
  if (typeof window === "undefined") {
    return next;
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Quota exceeded or storage blocked — the in-memory value returned here
    // still drives this page view.
  }
  return next;
}
